import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { releaseIndex } from "@/data/changelog";

const STORAGE_KEY = "bldr:last-seen-version";

export function useNewRelease() {
  const navigate = useNavigate();
  const toastFired = useRef(false);
  const latestVersion = releaseIndex[0]?.version;
  const latestTitle = releaseIndex[0]?.title;

  const [lastSeen, setLastSeen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const hasNewRelease = useMemo(
    () => !!latestVersion && lastSeen !== latestVersion,
    [latestVersion, lastSeen]
  );

  const markSeen = useCallback(() => {
    if (!latestVersion) return;
    try {
      localStorage.setItem(STORAGE_KEY, latestVersion);
    } catch {
      /* noop */
    }
    setLastSeen(latestVersion);
  }, [latestVersion]);

  useEffect(() => {
    if (!hasNewRelease || toastFired.current) return;
    toastFired.current = true;

    toast(`What's new in v${latestVersion}`, {
      description: latestTitle,
      action: {
        label: "View changelog",
        onClick: () => {
          markSeen();
          void navigate({ to: "/dashboard/changelog" });
        },
      },
      duration: 8000,
      position: "top-center",
    });
  }, [hasNewRelease, latestVersion, latestTitle, markSeen, navigate]);

  return { hasNewRelease, markSeen };
}
